<script setup lang="ts">
import {onMounted, onUnmounted, ref} from "vue";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {CardlessDetectionSetting} from "@/core/interfaces/detection/detection";
import {Button} from "@/core/components/ui/button";
import {ScanSearch, Trash2} from "@lucide/vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";

export type CardlessDebugInfo = {
    /** Raw OCR text */
    text: string;
    /** PNG buffer of the cropped image used for OCR */
    buffer: Buffer;
}

export type CalibratorInfo = {
    /** Raw OCR text */
    config: CardlessDetectionSetting[];
    /** PNG buffer of the calibrator */
    image: Buffer;
}

type CaptureInfo = {
    /** Width of the captured image */
    width: number;
    /** Height of the captured image */
    height: number;
    /** Buffer of the captured image */
    buffer: Buffer;
    /** Screen dimensions at the time of capture */
    screen: Display;
    /** Window dimensions at the time of capture */
    margins: {top: number, left: number};
};

interface Display {
    /** The scale factor of the display */
    scaleFactor: number,
    /** The work area size of the display, without the task bar. */
    workAreaSize: { width: number, height: number }
    /** The size of the display */
    size: { width: number, height: number }
}

const config = ref<CardlessDetectionSetting[]>();
const imageSources = ref<Record<string, CardlessDebugInfo>>({});
const errorMessage = ref<string | null>(null);
const capturedInfo = ref<CaptureInfo>();
const calibratorInfo = ref<CalibratorInfo | null>();
const calibrating = ref<boolean>(false);

/**
 * Clear all IPC listeners to prevent memory leaks.
 */
const clearListeners = () => {
    window.ipcRenderer.removeAllListeners('cardless-detection-captured');
    window.ipcRenderer.removeAllListeners('cardless-detection-error');
};

onMounted(async () => {
    config.value = await window.ipcRenderer.invoke('cardless-detection-get-crop-config');

    clearListeners();

    window.ipcRenderer.on('cardless-detection-captured', async (
        _: Event,
        fieldSlug: string,
        capturedData: CardlessDebugInfo,
        captureInfo: CaptureInfo
    ) => {
        imageSources.value[fieldSlug] = capturedData;
        capturedInfo.value = captureInfo;
    });

    window.ipcRenderer.on('cardless-detection-error', async (_: Event, message: string | null) => {
        errorMessage.value = message;
    });
});

onUnmounted(() => {
    clearListeners();
});

/**
 * Debounced input handler to update crop configuration.
 */
const onInput = (payload: string, index: number, key: string) => {
    config.value![index][key] = payload;

    window.ipcRenderer.invoke('cardless-detection-set-crop-config', JSON.stringify(config.value));
};

/**
 * Computed property to convert the current image buffer to a base64 string for display.
 */
const fieldImageSource = (fieldSlug: string): string => {
    const blob = new Blob([imageSources.value[fieldSlug].buffer] as BlobPart[], {type: "image/jpeg"});
    return URL.createObjectURL(blob);
};

/**
 * Computed property to convert the captured image buffer to a base64 string for display.
 */
const capturedImageSource = (): string => {
    if (calibratorInfo?.value?.image) {
        const blob = new Blob([calibratorInfo.value.image] as BlobPart[], {type: "image/jpeg"});
        return URL.createObjectURL(blob);
    }

    const blob = new Blob([capturedInfo!.value!.buffer] as BlobPart[], {type: "image/jpeg"});
    return URL.createObjectURL(blob);
};

/** Clear user configuration by invoking the appropriate IPC method. */
const clearUserConfig = async (): Promise<void> => {
    config.value = await window.ipcRenderer.invoke('cardless-detection-clear-crop-config');
    calibratorInfo.value = undefined;
};

/** Launch auto-calibration. */
const calibrate = async (): Promise<void> => {
    calibrating.value = true;
    calibratorInfo.value = await window.ipcRenderer.invoke('cardless-detection-calibrate');

    config.value = await window.ipcRenderer.invoke('cardless-detection-get-crop-config');
    calibrating.value = false;
};
</script>

<template>
  <div class="flex flex-col gap-2 p-4 bg-white h-full">
    <div class="flex items-center gap-4">
      <div class="flex min-w-[750px] h-[400px]">
        <img v-if="capturedInfo?.buffer || calibratorInfo?.image" :src="capturedImageSource()" alt="Image détectée" class="h-full">
        <div v-else class="w-full h-full bg-gray" />
      </div>
      <div class="flex flex-col items-start gap-4 h-full">
        <div class="flex flex-col gap-2">
          <Button variant="destructive" @click="clearUserConfig">
            <Trash2 />
            Réinitialiser
          </Button>
          <Button variant="outline" @click="calibrate">
            <div v-if="calibrating">
              <BaseSpinner />
            </div>
            <div v-else class="flex gap-2 items-center">
              <ScanSearch />
              Calibrer
            </div>
          </Button>
        </div>
        <div v-if="errorMessage" class="p-2 bg-red-100 text-red-700 rounded">
          {{ errorMessage }}
        </div>
        <div class="text-sm h-12">
          <div v-if="capturedInfo">
            <strong>Taille de la capture:</strong> {{ capturedInfo.width }} x {{ capturedInfo.height }} px
          </div>
          <div v-if="capturedInfo">
            <strong>Taille de l'écran :</strong>
            {{ capturedInfo.screen.workAreaSize.width }} x {{ capturedInfo.screen.workAreaSize.height }} px
          </div>
          <div v-if="capturedInfo">
            <strong>Marges</strong>
            {{ capturedInfo.margins.left }} x
            {{ capturedInfo.margins.top }} px
            <strong>- DPI</strong>
            {{ Math.round(capturedInfo.screen.scaleFactor * 100) }} %
          </div>
        </div>
      </div>
    </div>
    <div class="flex gap-2">
      <div v-for="(fieldConfig, index) of config" :key="fieldConfig.slug" class="flex flex-1 flex-col gap-2">
        <div class="flex flex-col gap-2 bg-slate-50 rounded-lg p-2">
          <h3>{{ fieldConfig.name }}</h3>
          <div
            v-for="(label, key) in {'x': 'x', 'y':'y', 'width':'l', 'height': 'h'}" :key="key"
            class="flex items-center gap-2"
          >
            <Label><strong>{{ label }}</strong></Label>
            <Input
              v-if="config"
              :model-value="config[index][key] as string"
              class="flex-1"
              @update:model-value="(payload) => onInput(payload as string, index, key)"
            />
          </div>
        </div>
        <div>
          <div class="flex items-center justify-start">
            <img
              v-if="imageSources[fieldConfig.slug]"
              :src="fieldImageSource(fieldConfig.slug as string)"
              alt="Image détectée"
              style="height: 20px"
            >
          </div>
          <div class="text-sm p-2 text-primary">
            {{ imageSources[fieldConfig.slug]?.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
